// react
import React from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

// material ui
import {
  Box,
  Button,
  ButtonGroup,
  ClickAwayListener,
  Divider,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Typography,
} from '@mui/material';

// shared components
import { WritableOnly } from '@/components/access-control';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

export default function InitiativeActionCell({
  row,
  energyGroup,
  onView,
  onEdit,
  onDelete,
  onResetDates,
}) {
  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const { t } = useTranslation('energyxplorer-initiatives-tab');

  const isSuccess = row?.data?.Success;

  const unavailable =
    (energyGroup?.options.state === 'Desynced' && !isSuccess) ||
    (energyGroup?.options.state === 'Processing' && !isSuccess);

  const buttonContent = unavailable
    ? 'Beregner...'
    : isSuccess
      ? t('button_labels.show_plot')
      : 'Fejl';

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorEl && anchorEl.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleMenuItemClick = (option) => {
    setOpen(false);
    if (option === 'view') {
      onView(row);
    }
    if (option === 'edit') {
      onEdit(row);
    }
    if (option === 'delete') {
      onDelete(row);
    }
    if (option === 'resetDates') {
      onResetDates(row);
    }
  };

  const dispatch = useDispatch();

  const handleIntermediateState = () => {
    if (unavailable) {
      dispatch(
        showSnackbar({
          message:
            'Initiativet er stadig under beregning. Prøv igen om et øjeblik.',
          severity: 'info',
        }),
      );
    } else if (!isSuccess) {
      dispatch(
        showSnackbar({
          message:
            'Der opstod en fejl under generering af initiativets prognose. Hvis fejlen fortsætter, kontakt venligst support.',
          severity: 'warning',
        }),
      );
    }
  };

  return (
    <>
      <ButtonGroup
        variant="outlined"
        size="small"
        ref={setAnchorEl}
        aria-label="split button"
        sx={{
          '& .MuiButtonGroup-grouped:not(:last-of-type)': {
            borderRight: 'none',
          },
        }}
      >
        <Button
          data-testid={`initiative-action-view-${row?.id}`}
          startIcon={
            unavailable ? (
              <AutorenewRoundedIcon
                color="info"
                sx={{
                  animation: 'spin 1.5s linear infinite',
                  '@keyframes spin': {
                    '0%': {
                      transform: 'rotate(0deg)',
                    },
                    '100%': {
                      transform: 'rotate(360deg)',
                    },
                  },
                }}
              />
            ) : isSuccess ? (
              <TimelineRoundedIcon />
            ) : (
              <WarningRoundedIcon color="warning" />
            )
          }
          size="small"
          onClick={() =>
            isSuccess ? handleMenuItemClick('view') : handleIntermediateState()
          }
          sx={{
            minWidth: 75,
            borderColor: 'divider',
            ':hover': { borderColor: 'divider' },
          }}
        >
          {buttonContent}
        </Button>
        <Button
          data-testid={`initiative-action-menu-toggle-${row?.id}`}
          size="small"
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="menu"
          onClick={handleToggle}
          sx={{
            borderColor: 'divider',
            ':hover': { borderColor: 'divider' },
          }}
        >
          <ArrowDropDownIcon fontSize="small" />
        </Button>
      </ButtonGroup>
      <Popper
        open={open}
        anchorEl={anchorEl}
        role={undefined}
        transition
        modifiers={[
          {
            name: 'flip',
            options: { fallbackPlacements: ['top-start', 'bottom-start'] },
          },
          {
            name: 'preventOverflow',
            options: { altBoundary: true, rootBoundary: 'document' },
          },
        ]}
        style={{ zIndex: 1301 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              zIndex: 21302,
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <WritableOnly>
                  <MenuList
                    id="split-button-menu"
                    autoFocusItem
                    dense
                    sx={{ p: 1 }}
                  >
                    <MenuItem
                      data-testid={`initiative-action-reset-${row?.id}`}
                      onClick={() => handleMenuItemClick('resetDates')}
                      sx={{
                        py: '6px',
                        px: '12px',
                        borderRadius: '5px',
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <AutorenewRoundedIcon sx={{ mr: 1 }} />
                        <Typography variant="body2" fontWeight="medium">
                          {t('actions_cell.reset_initiative')}
                        </Typography>
                      </Box>
                    </MenuItem>
                    <Divider />
                    <MenuItem
                      data-testid={`initiative-action-edit-${row?.id}`}
                      onClick={() => handleMenuItemClick('edit')}
                      sx={{
                        py: '6px',
                        px: '12px',
                        borderRadius: '5px',
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <EditRoundedIcon sx={{ mr: 1 }} />
                        <Typography variant="body2" fontWeight="medium">
                          {t('button_labels.edit')}
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem
                      data-testid={`initiative-action-delete-${row?.id}`}
                      onClick={() => handleMenuItemClick('delete')}
                      sx={{
                        py: '6px',
                        px: '12px',
                        borderRadius: '5px',
                        ':hover': { color: 'error.main' },
                        transition: 'color 0.2s ease-in-out',
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <DeleteRoundedIcon sx={{ mr: 1 }} />
                        <Typography variant="body2" fontWeight="medium">
                          {t('button_labels.delete')}
                        </Typography>
                      </Box>
                    </MenuItem>
                  </MenuList>
                </WritableOnly>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}
